import { useEffect, useRef, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from 'react-query'

import AdxDropdown from 'components/custom/AdxDropdown'
import AdxInput from 'components/custom/AdxInput'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxPhoneNumber from 'components/reusable/AdxPhoneNumber/AdxPhoneNumber'
import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'

import Astronaut from '../assets/astronaut.svg'

import { companyTypes, countries } from 'services/api/dropdown'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { registerOrganisation } from 'services/api/signup'
import { hasToken, removeAllCookies } from 'services/helper/cookiesHelper'
import { Info } from '@mui/icons-material'
import AdxButton from 'components/custom/AdxButton'

const SignUpForm = () => {
  const { state } = useLocation()
  const navigate = useNavigate()

  // revoke token when exit page
  // redirect if the user got no cookies
  useEffect(() => {
    if (!hasToken()) {
      navigate('/signup')
    }
  }, [navigate])

  // navigation states
  const companyDetails = state?.dataForm

  // form states
  const [arrayOfStates, setArrayOfStates] = useState([])
  const formikRef = useRef()
  const { data: companyTypesQuery } = useQuery('companyTypesQuery', companyTypes)
  const { data: countriesQuery } = useQuery('countriesQuery', countries)

  // state and function for AdxDropdown components
  const [selectValueCompanyType, setSelectValueCompanyType] = useState('')
  const [selectValueState, setSelectValueState] = useState('')
  const [selectValueCountry, setSelectValueCountry] = useState('')

  const handleSetSelectValueCompanyType = (event) => {
    setSelectValueCompanyType(event.target.value)
  }

  const handleSetSelectState = (event) => {
    setSelectValueState(event.target.value)
  }

  const handleSetSelectCountry = (event) => {
    setSelectValueCountry(event?.target?.value)
  }

  useEffect(() => {
    setSelectValueCompanyType(companyDetails?.company_type)
    setSelectValueCountry(companyDetails?.country ?? 'Malaysia')
    setSelectValueState(companyDetails?.state)
    let states = countriesQuery?.data.find((item) => item.name === 'Malaysia')?.states
    setArrayOfStates(states)
  }, [companyDetails, countriesQuery])

  const initialValues = {
    organization_name: companyDetails?.organization_name,
    company_type: companyDetails?.company_type,
    registration_no: companyDetails?.registration_no,
    mobile_no: companyDetails?.mobile_number,
    company_phone_country_id: companyDetails?.company_phone_country_id || '132',
    address: companyDetails?.address,
    postal_code: companyDetails?.postcode,
    state: companyDetails?.state,
    country: companyDetails?.country || 'Malaysia',
  }

  // form validation schema
  const validationSchema = Yup.object().shape({
    organization_name: Yup.string().required('Company name is required'),
    company_type: Yup.number().required('Company type is required'),
    registration_no: Yup.string()
      .required('Registration number is required')
      .matches(/^[a-zA-Z0-9-]+$/, 'Only alphabets are allowed for this field '),
    company_phone_country_id: Yup.string().required('Country code is required'),
    mobile_no: Yup.string()
      .typeError('Company phone required')
      .required('Company phone required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field '),
    address: Yup.string().required('Address is required'),
    postal_code: Yup.string()
      .required('Postal code is required')
      .matches(/^[0-9]+$/, 'Only numbers are allowed for this field '),
    state: Yup.string().required('State is required').nullable(),
    country: Yup.string().required('Country is required').nullable(),
  })

  // form action
  // react query mutate hook
  const { isLoading: submitLoading, mutate: submitOrganisation } = useMutation(
    (values) => registerOrganisation(values),
    {
      onSuccess: async (res) => {
        handleSubmitClose()
        removeAllCookies()
        navigate('/login')
      },
      onError: async (err) => {
        handleSubmitClose()
        const msg = Object.values(err?.data?.errors || [])[0] || 'An error occured'
        setErrorMessage(msg)
        handleErrorOpen()
      },
    }
  )

  // submit modal
  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false)
  const handleSubmitOpen = () => {
    setSubmitModalOpen(true)
  }
  const handleSubmitClose = () => {
    setSubmitModalOpen(false)
  }
  const handleOnSubmit = async () => {
    submitOrganisation(formikRef.current.values)
  }

  // error modal
  const [isErrorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('An error occurred')
  const handleErrorOpen = () => {
    setErrorModalOpen(true)
  }
  const handleErrorClose = () => {
    setErrorModalOpen(false)
  }

  return (
    <>
      <OnboardingContainer
        bgColorRight="alternate.main"
        topRightPhoto="10%"
        leftRightPhoto="75%"
        heightRightPhoto="100%"
        transformRightPhoto={`translate(${-50}%, ${0}%)`}
        sx={{ height: 'auto', overflowX: 'hidden' }}
        rightComponent={
          <Box
            component="img"
            src={Astronaut}
            position="fixed"
            bottom={0}
            right={0}
            width="calc(100vw * 0.55)"
            display={{ xs: 'none', lg: 'block' }}
          />
        }
      >
        <Box
          height={'100%'}
          display="flex"
          justifyContent={{ xs: 'center', lg: 'flex-end' }}
          alignItems={{ xs: 'center', lg: 'center' }}
        >
          <Box
            width={{ xs: '100%', lg: '500px' }}
            mr={{ lg: 7.6875 }}
            p={{ xs: 2, lg: 0 }}
            // py={{ xs: '30px', lg: '50px' }}
            position="relative"
            // top={100}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Box mb={4}>
              <Typography variant="h1" mb={1}>
                Organization details
              </Typography>
              <Typography variant="body1" textAlign="justify" color="light-gray-text.main">
                Tell us a little bit about your organization. This will help us to onboard your
                account based on your actual organization data.
              </Typography>
            </Box>
            <Formik
              enableReinitialize
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmitOpen}
              innerRef={formikRef}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleSubmit,
                handleBlur,
                setFieldValue,
              }) => (
                <Form>
                  <Box px="2px">
                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={12}>
                        <Field
                          as={AdxInput}
                          name="organization_name"
                          label="Company name"
                          size="standard"
                          placeholder="Company name"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errorMessage={
                            <ErrorMessage name="organization_name" component={AdxErrorMessage} />
                          }
                          isErrorShown={errors.organization_name && touched.organization_name}
                        />
                      </Grid>
                      <Grid item xs={12} lg={12}>
                        <Field
                          as={AdxDropdown}
                          name="company_type"
                          label="Company type"
                          value={selectValueCompanyType}
                          onChange={(e) => {
                            handleSetSelectValueCompanyType(e)
                            setFieldValue('company_type', e.target.value)
                          }}
                          onBlur={handleBlur}
                          isErrorShown={errors.company_type && touched.company_type}
                          errorMessage={
                            <ErrorMessage name="company_type" component={AdxErrorMessage} />
                          }
                        >
                          {companyTypesQuery?.data?.company_types.map((item) => (
                            <AdxMenuItem
                              key={item.id}
                              value={item.id}
                              selectedvalue={selectValueCompanyType}
                            >
                              {item.description}
                            </AdxMenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={12} lg={12}>
                        <Field
                          as={AdxInput}
                          name="registration_no"
                          label="Registration Number"
                          size="standard"
                          placeholder="Registration Number"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errorMessage={
                            <ErrorMessage name="registration_no" component={AdxErrorMessage} />
                          }
                          isErrorShown={errors.registration_no && touched.registration_no}
                        />
                      </Grid>
                      <Grid item xs={12} lg={12}>
                        <AdxPhoneNumber
                          label="Primary Phone Number"
                          placeholder="E.g: 123456789"
                          sxInput={{ width: '100%' }}
                          selectedCountries={['Malaysia', 'Indonesia', 'Singapore']}
                          dropdownValue={values?.company_phone_country_id}
                          dropdownDisabled
                          dropdownName="company_phone_country_id"
                          onDropdownChange={(value) =>
                            setFieldValue(`company_phone_country_id`, value)
                          }
                          dropdownErrorMessage={
                            touched.company_phone_country_id && errors.company_phone_country_id
                          }
                          inputValue={values?.mobile_no}
                          inputName="mobile_no"
                          onInputChange={handleChange}
                          inputErrorMessage={touched.mobile_no && errors.mobile_no}
                          dropdownIsErrorShown={
                            touched.company_phone_country_id && errors.company_phone_country_id
                          }
                          inputIsErrorShown={touched.mobile_no && errors.mobile_no}
                        />
                      </Grid>
                      <Grid item xs={12} lg={12}>
                        <Field
                          as={AdxInput}
                          name="address"
                          label="Primary address"
                          size="standard"
                          placeholder="Primary address"
                          multiline
                          minRows={3}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errorMessage={<ErrorMessage name="address" component={AdxErrorMessage} />}
                          isErrorShown={errors.address && touched.address}
                        />
                      </Grid>
                      <Grid item xs={12} lg={12}>
                        <Field
                          as={AdxDropdown}
                          name="country"
                          label="Country"
                          value={values?.country}
                          disabled
                          onChange={(e) => {
                            handleSetSelectCountry(e)
                            setFieldValue('country', e.target.value)
                            setFieldValue('state', null)
                            setArrayOfStates(
                              countriesQuery?.data.find((item) => item.name === e.target.value)
                                .states
                            )
                          }}
                          isErrorShown={errors?.country && touched?.country}
                          errorMessage={<ErrorMessage name="country" component={AdxErrorMessage} />}
                        >
                          {countriesQuery?.data
                            .filter((country) => {
                              if (
                                country.name === 'Indonesia' ||
                                country.name === 'Malaysia' ||
                                country.name === 'Singapore'
                              ) {
                                return country
                              } else {
                                return null
                              }
                            })
                            ?.map((item, index) => (
                              <AdxMenuItem
                                key={index}
                                value={item.name}
                                selectedvalue={selectValueCountry}
                              >
                                {item.name}
                              </AdxMenuItem>
                            ))}
                        </Field>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Field
                          as={AdxInput}
                          name="postal_code"
                          label="Postcode"
                          size="standard"
                          placeholder="Postcode"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          errorMessage={
                            <ErrorMessage name="postal_code" component={AdxErrorMessage} />
                          }
                          isErrorShown={errors.postal_code && touched.postal_code}
                        />
                      </Grid>
                      <Grid item xs={12} lg={8}>
                        <Field
                          as={AdxDropdown}
                          name="state"
                          label="State"
                          value={selectValueState}
                          disabled={Boolean(!arrayOfStates)}
                          onChange={(e) => {
                            setFieldValue('state', e.target.value)
                            handleSetSelectState(e)
                          }}
                          isErrorShown={errors.state && touched.state}
                          errorMessage={<ErrorMessage name="state" component={AdxErrorMessage} />}
                        >
                          {arrayOfStates?.map((item, index) => (
                            <AdxMenuItem
                              key={index}
                              value={item.name}
                              selectedvalue={selectValueState}
                            >
                              {item.name}
                            </AdxMenuItem>
                          ))}
                        </Field>
                      </Grid>
                      <Grid item xs={12} lg={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '14px',
                            borderRadius: '4.5px',
                            backgroundColor: 'default-background.main',
                            p: '14px',
                          }}
                        >
                          <Info sx={{ color: 'alternate.main', fontSize: 20 }} />
                          <Typography sx={{ color: 'light-gray-text.main' }}>
                            adnexiohr is currently only available in Malaysia. We are working on
                            making this feature available in other countries soon.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box mt={5} display="flex" gap={2}>
                      <AdxButton variant={'contained'} fullWidth onClick={handleSubmit}>
                        Save
                      </AdxButton>
                    </Box>
                  </Box>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </OnboardingContainer>

      {/* submit modal */}
      <AdxInfoModal
        variant={'standard'}
        open={isSubmitModalOpen}
        onClose={handleSubmitClose}
        handleClose={handleSubmitClose}
        iconColor="alternate.main"
        title="Confirm submission?"
        subtitle={null}
        footerButtons
        firstBtnLabel={'Ok'}
        firstBtnHandle={handleOnSubmit}
        secondBtnLabel={'Back'}
        secondBtnHandle={handleSubmitClose}
      >
        {/* <AdxButton
          variant={'contained'}
          fullWidth
          onClick={handleOnSubmit}
          isLoading={submitLoading}
          disabled={submitLoading}
        >
          Ok
        </AdxButton>
        <AdxButton variant={'ghost'} fullWidth onClick={handleSubmitClose}>
          Back
        </AdxButton> */}
      </AdxInfoModal>

      {/* error modal */}
      <AdxInfoModal
        open={isErrorModalOpen}
        onClose={handleErrorClose}
        handleClose={handleErrorClose}
        iconColor="error.main"
        muiIcon="error"
        title={errorMessage}
        subtitle={null}
        footerButtons
        firstBtnLabel={'Ok'}
        firstBtnHandle={handleErrorClose}
      ></AdxInfoModal>
    </>
  )
}

export default SignUpForm
